import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { Button } from "../../components/Button";
import { Flex } from "../../components/Flex";
import { Input } from "../../components/Input";
import { SubHeader } from "../../components/SubHeader";
import { Text } from "../../components/Text";
import { Textarea } from "../../components/Textarea";

import { integrationActions } from "../../redux/integration/integration.slice";
import { useAppDispatch, useAppSelector } from "../../redux/store";
import { userSliceSelectors } from "../../redux/user/user.selector";
import { Spacing } from "../../theme/Spacing";
import { PageIcons } from "../../utils/GlobalIcons";
import { IntegrationContextProvider } from "./context/integration-context";

const ContentWrapper = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.m};
  padding: 0 ${Spacing.m} ${Spacing.m};
`;

const Panel = styled(Flex)`
  flex-direction: column;
  gap: ${Spacing.m};
  padding: ${Spacing.l};
  border: ${({ theme }) => `1px solid ${theme.lightgray}`};
  border-radius: 1rem;
  background: ${({ theme }) => theme.white};
`;

const ResponsivePanels = styled(Flex)`
  gap: ${Spacing.m};
  flex-wrap: wrap;
`;

const Checklist = styled.ol`
  margin: 0;
  padding-inline-start: 1.25rem;
  color: ${({ theme }) => theme.textColor};

  li + li {
    margin-top: ${Spacing.s};
  }
`;

const MetaRow = styled(Flex)`
  justify-content: space-between;
  gap: ${Spacing.m};
  padding-bottom: ${Spacing.s};
  border-bottom: ${({ theme }) => `1px solid ${theme.lightgray}`};

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

type SendResult = {
  body: string;
  dateCreated?: string;
  from: string;
  sid: string;
  status?: string;
  to: string;
};

const WhatsAppAIChatReviewPageContent: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const userId = useAppSelector(userSliceSelectors.selectLoggedInUserId);
  const [recipientPhoneNumber, setRecipientPhoneNumber] =
    useState("+90537 921 69 03");
  const [message, setMessage] = useState(
    t("integration.whatsappAiReview.defaultMessage", {
      defaultValue:
        "Hello from ATIRA SPACE. This is our WhatsApp review demo message.",
    }),
  );
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState<SendResult | null>(null);

  useEffect(() => {
    if (!userId) {
      return;
    }

    dispatch(integrationActions.getUserIntegrations({ userId }));
  }, [dispatch, userId]);

  const handleSend = async (templateSID?: string) => {};

  return (
    <Flex flexDirection="column">
      <SubHeader
        title={t("integration.whatsappAiReview.title", {
          defaultValue: "WhatsApp Review Demo",
        })}
        icon={PageIcons.Integration}
      />

      <ContentWrapper>
        <ResponsivePanels>
          <Panel flex="1 1 34rem" minWidth="20rem">
            <Text fontSize="l" fontWeight={600}>
              1) Send an approved template message:
            </Text>
            <Text>
              Messages will be sent from this number:{" "}
              <mark>{process.env.REACT_APP_WHATSAPP_META_TEMP_PHONE}</mark>
            </Text>

            <Text>
              the verified registered recipient number that was tested is:{" "}
              <mark>+90537 921 69 03</mark>
            </Text>

            <Input
              title={"Recipient WhatsApp number"}
              type="tel"
              value={recipientPhoneNumber}
              onChange={(e) => setRecipientPhoneNumber(e.target.value)}
              placeholder={t(
                "integration.whatsappAiReview.form.recipientPlaceholder",
              )}
              required
            />

            <Text>
              The template will be sent is: <mark>hello_world</mark>
            </Text>

            <Button
              onClick={() => handleSend("hello_world")}
              loading={sending}
              disabled={!recipientPhoneNumber.trim() || !message.trim()}
              width="fit-content"
            >
              {t("common.send")}
            </Button>
          </Panel>
        </ResponsivePanels>

        <ResponsivePanels>
          <Panel flex="1 1 34rem" minWidth="20rem">
            <Text fontSize="l" fontWeight={600}>
              2) After the user responds, send a free form messsage
            </Text>

            <Input
              title={t("integration.whatsappAiReview.form.recipient", {
                defaultValue: "Recipient WhatsApp number",
              })}
              type="tel"
              value={recipientPhoneNumber}
              onChange={(e) => setRecipientPhoneNumber(e.target.value)}
              placeholder={t(
                "integration.whatsappAiReview.form.recipientPlaceholder",
                {
                  defaultValue: "+905551112233",
                },
              )}
              required
            />

            <Textarea
              title={t("integration.whatsappAiReview.form.message", {
                defaultValue: "Message",
              })}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder={t(
                "integration.whatsappAiReview.form.messagePlaceholder",
                {
                  defaultValue:
                    "Hello from ATIRA SPACE. This is our WhatsApp review demo message.",
                },
              )}
              rows={7}
              maxHeight="16rem"
              required
            />

            <Button
              onClick={() => handleSend()}
              loading={sending}
              disabled={!recipientPhoneNumber.trim() || !message.trim()}
              width="fit-content"
            >
              {t("common.send")}
            </Button>
          </Panel>
        </ResponsivePanels>

        {lastResult ? (
          <Panel>
            <Text fontSize="l" fontWeight={600}>
              {t("integration.whatsappAiReview.result.title", {
                defaultValue: "Last send result",
              })}
            </Text>

            <MetaRow>
              <Text color="darkerSub">SID</Text>
              <Text>{lastResult.sid}</Text>
            </MetaRow>
            <MetaRow>
              <Text color="darkerSub">
                {t("integration.whatsappAiReview.result.from", {
                  defaultValue: "From",
                })}
              </Text>
              <Text>{lastResult.from}</Text>
            </MetaRow>
            <MetaRow>
              <Text color="darkerSub">
                {t("integration.whatsappAiReview.result.to", {
                  defaultValue: "To",
                })}
              </Text>
              <Text>{lastResult.to}</Text>
            </MetaRow>
            <MetaRow>
              <Text color="darkerSub">
                {t("integration.whatsappAiReview.result.status", {
                  defaultValue: "Status",
                })}
              </Text>
              <Text>{lastResult.status || t("common.ready")}</Text>
            </MetaRow>
            <MetaRow>
              <Text color="darkerSub">
                {t("integration.whatsappAiReview.result.body", {
                  defaultValue: "Body",
                })}
              </Text>
              <Text>{lastResult.body}</Text>
            </MetaRow>
            {lastResult.dateCreated ? (
              <MetaRow>
                <Text color="darkerSub">
                  {t("integration.whatsappAiReview.result.createdAt", {
                    defaultValue: "Created at",
                  })}
                </Text>
                <Text>{lastResult.dateCreated}</Text>
              </MetaRow>
            ) : null}
          </Panel>
        ) : null}
      </ContentWrapper>
    </Flex>
  );
};

/** @deprecated */
export const WhatsAppAIChatReviewRoute: React.FC = () => {
  return (
    <IntegrationContextProvider>
      <WhatsAppAIChatReviewPageContent />
    </IntegrationContextProvider>
  );
};
